import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import {
  ConferenceDto,
  ConferenceMetricsDto,
  ConnectionTimeSeriesDto,
  ConnectionCategoryDto,
  SessionDto,
  ConferenceLogDto,
  BusinessConnectionsLeaderboardDto,
  AttendeeConnectionsLeaderboardDto,
} from '../dto/conference.dto';
import {
  QueryResult,
  QueryOptions,
  ProcessResult,
  QueryResultItem,
} from '../components/common-dto/query.dto';

@Injectable({
  providedIn: 'root',
})
export class MockConferenceService {
  private mockConferences: QueryResultItem<ConferenceDto>[] = [
    {
      id: '1',
      item: {
        name: 'Tech Innovation Summit 2024',
        location: 'Omaha, NE',
        date: new Date('2024-12-15'),
        startDate: new Date('2024-12-15T09:00:00'),
        endDate: new Date('2024-12-17T17:00:00'),
        isActive: true,
        isUpcoming: false,
        createdAt: new Date('2024-01-01'),
        updatedAt: new Date('2024-12-15'),
      },
    },
    {
      id: '2',
      item: {
        name: 'Digital Marketing Conference',
        location: 'Lincoln, NE',
        date: new Date('2024-12-20'),
        startDate: new Date('2024-12-20T08:00:00'),
        endDate: new Date('2024-12-22T16:00:00'),
        isActive: true,
        isUpcoming: false,
        createdAt: new Date('2024-02-01'),
        updatedAt: new Date('2024-12-20'),
      },
    },
    {
      id: '3',
      item: {
        name: 'AI & Machine Learning Expo',
        location: 'Kearney, NE',
        date: new Date('2025-01-10'),
        startDate: new Date('2025-01-10T09:00:00'),
        endDate: new Date('2025-01-12T17:00:00'),
        isActive: false,
        isUpcoming: true,
        createdAt: new Date('2024-03-01'),
        updatedAt: new Date('2024-12-01'),
      },
    },
    {
      id: '4',
      item: {
        name: 'Cloud Computing Summit',
        location: 'Grand Island, NE',
        date: new Date('2025-02-05'),
        startDate: new Date('2025-02-05T08:00:00'),
        endDate: new Date('2025-02-07T16:00:00'),
        isActive: false,
        isUpcoming: true,
        createdAt: new Date('2024-04-01'),
        updatedAt: new Date('2024-12-01'),
      },
    },
    {
      id: '5',
      item: {
        name: 'Startup Ecosystem Conference',
        location: 'Omaha, NE',
        date: new Date('2025-03-15'),
        startDate: new Date('2025-03-15T09:00:00'),
        endDate: new Date('2025-03-17T17:00:00'),
        isActive: false,
        isUpcoming: true,
        createdAt: new Date('2024-05-01'),
        updatedAt: new Date('2024-12-01'),
      },
    },
  ];

  private currentConferenceId: string | null = null;

  constructor() {}

  getConferences(queryParams: QueryOptions): Observable<QueryResult<ConferenceDto>> {
    let items = [...this.mockConferences];

    // Sort: active conferences first, then upcoming
    items.sort((a, b) => {
      if (a.item.isActive && !b.item.isActive) return -1;
      if (!a.item.isActive && b.item.isActive) return 1;
      if (a.item.isUpcoming && !b.item.isUpcoming) return -1;
      if (!a.item.isUpcoming && b.item.isUpcoming) return 1;
      return a.item.date.getTime() - b.item.date.getTime();
    });

    // Apply pagination
    if (queryParams.take && queryParams.skip !== undefined) {
      const skip = Number(queryParams.skip);
      const take = Number(queryParams.take);
      items = items.slice(skip, skip + take);
    }

    return of({
      items,
      total: this.mockConferences.length,
      take: queryParams.take || items.length,
      skip: queryParams.skip || 0,
    }).pipe(delay(300));
  }

  getConference(id: string): Observable<ConferenceDto> {
    const conference = this.mockConferences.find((c) => c.id === id);
    if (conference) {
      return of(conference.item).pipe(delay(200));
    }
    return of(this.mockConferences[0].item).pipe(delay(200));
  }

  joinConference(conferenceId: string): Observable<ProcessResult> {
    this.currentConferenceId = conferenceId;
    return of({
      success: true,
      message: 'Successfully joined conference',
      id: conferenceId,
    }).pipe(delay(500));
  }

  leaveConference(conferenceId: string): Observable<ProcessResult> {
    this.currentConferenceId = null;
    return of({
      success: true,
      message: 'Successfully left conference',
      id: conferenceId,
    }).pipe(delay(500));
  }

  getCurrentConferenceId(): string | null {
    return this.currentConferenceId;
  }

  getConferenceMetrics(conferenceId: string): Observable<ConferenceMetricsDto> {
    // Generate different metrics based on conference ID
    const baseMetrics = {
      '1': { attendees: 1250, vendors: 45, connections: 320 },
      '2': { attendees: 980, vendors: 32, connections: 245 },
      '3': { attendees: 0, vendors: 0, connections: 0 },
      '4': { attendees: 0, vendors: 0, connections: 0 },
      '5': { attendees: 0, vendors: 0, connections: 0 },
    };

    const metrics = baseMetrics[conferenceId as keyof typeof baseMetrics] || {
      attendees: 500,
      vendors: 20,
      connections: 150,
    };

    return of({
      conferenceId,
      numberOfAttendees: metrics.attendees,
      numberOfVendors: metrics.vendors,
      numberOfConnections: metrics.connections,
    }).pipe(delay(300));
  }

  getConnectionsOverTime(conferenceId: string): Observable<ConnectionTimeSeriesDto[]> {
    // Generate mock time series data for a single day (hourly)
    const data: ConnectionTimeSeriesDto[] = [];
    const hours = [
      '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
      '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM',
      '6:00 PM', '7:00 PM', '8:00 PM', '9:00 PM', '10:00 PM',
      '11:00 PM', '12:00 AM', '1:00 AM', '2:00 AM', '3:00 AM',
      '4:00 AM', '5:00 AM', '6:00 AM', '7:00 AM'
    ];

    // Generate realistic connection data throughout the day
    // Peak hours are typically mid-day (10 AM - 4 PM)
    hours.forEach((hour, index) => {
      let connections = 0;
      if (index >= 2 && index <= 8) {
        // Peak hours (10 AM - 4 PM)
        connections = Math.floor(Math.random() * 30) + 40;
      } else if (index >= 9 && index <= 11) {
        // Afternoon/Evening (5 PM - 7 PM)
        connections = Math.floor(Math.random() * 20) + 25;
      } else if (index >= 0 && index <= 1) {
        // Morning (8 AM - 9 AM)
        connections = Math.floor(Math.random() * 15) + 15;
      } else {
        // Late evening/Night (8 PM - 7 AM)
        connections = Math.floor(Math.random() * 10) + 5;
      }

      data.push({
        date: hour,
        connections: connections,
      });
    });

    return of(data).pipe(delay(300));
  }

  getConnectionsByCategory(conferenceId: string): Observable<ConnectionCategoryDto[]> {
    // Generate mock category data using the same categories as posts
    const categories = [
      { category: 'IT', connections: 120 },
      { category: 'Construction', connections: 85 },
      { category: 'Medical', connections: 65 },
      { category: 'Manufacturing', connections: 45 },
      { category: 'Finance', connections: 35 },
      { category: 'Education', connections: 30 },
      { category: 'Agriculture', connections: 25 },
      { category: 'Retail', connections: 20 },
    ];

    return of(categories).pipe(delay(300));
  }

  getSessions(conferenceId: string, queryParams: QueryOptions): Observable<QueryResult<SessionDto>> {
    const mockSessions: QueryResultItem<SessionDto>[] = [
      {
        id: '1',
        item: {
          title: 'Introduction to Angular 18',
          presenter: 'Sarah Johnson',
          presenterAvatar: 'https://ui-avatars.com/api/?name=Sarah+Johnson&background=42A5F5&color=fff',
          room: 'Grand Ballroom A',
          date: new Date('2024-12-15'),
          time: '9:00 AM - 10:30 AM',
          conferenceId,
          createdAt: new Date('2024-12-01'),
          updatedAt: new Date('2024-12-01'),
        },
      },
      {
        id: '2',
        item: {
          title: 'Building Scalable APIs with Node.js',
          presenter: 'Michael Chen',
          presenterAvatar: 'https://ui-avatars.com/api/?name=Michael+Chen&background=66BB6A&color=fff',
          room: 'Conference Room B',
          date: new Date('2024-12-15'),
          time: '11:00 AM - 12:30 PM',
          conferenceId,
          createdAt: new Date('2024-12-01'),
          updatedAt: new Date('2024-12-01'),
        },
      },
      {
        id: '3',
        item: {
          title: 'Cloud Architecture Best Practices',
          presenter: 'Emily Rodriguez',
          presenterAvatar: 'https://ui-avatars.com/api/?name=Emily+Rodriguez&background=FFA726&color=fff',
          room: 'Tech Hub',
          date: new Date('2024-12-15'),
          time: '2:00 PM - 3:30 PM',
          conferenceId,
          createdAt: new Date('2024-12-01'),
          updatedAt: new Date('2024-12-01'),
        },
      },
      {
        id: '4',
        item: {
          title: 'Machine Learning Fundamentals',
          presenter: 'David Kim',
          presenterAvatar: 'https://ui-avatars.com/api/?name=David+Kim&background=AB47BC&color=fff',
          room: 'Innovation Lab',
          date: new Date('2024-12-16'),
          time: '9:00 AM - 10:30 AM',
          conferenceId,
          createdAt: new Date('2024-12-01'),
          updatedAt: new Date('2024-12-01'),
        },
      },
      {
        id: '5',
        item: {
          title: 'DevOps and CI/CD Pipelines',
          presenter: 'Jessica Martinez',
          presenterAvatar: 'https://ui-avatars.com/api/?name=Jessica+Martinez&background=EF5350&color=fff',
          room: 'Grand Ballroom A',
          date: new Date('2024-12-16'),
          time: '11:00 AM - 12:30 PM',
          conferenceId,
          createdAt: new Date('2024-12-01'),
          updatedAt: new Date('2024-12-01'),
        },
      },
      {
        id: '6',
        item: {
          title: 'React Performance Optimization',
          presenter: 'Robert Taylor',
          presenterAvatar: 'https://ui-avatars.com/api/?name=Robert+Taylor&background=26A69A&color=fff',
          room: 'Conference Room B',
          date: new Date('2024-12-16'),
          time: '2:00 PM - 3:30 PM',
          conferenceId,
          createdAt: new Date('2024-12-01'),
          updatedAt: new Date('2024-12-01'),
        },
      },
      {
        id: '7',
        item: {
          title: 'Database Design Patterns',
          presenter: 'Amanda White',
          presenterAvatar: 'https://ui-avatars.com/api/?name=Amanda+White&background=5C6BC0&color=fff',
          room: 'Tech Hub',
          date: new Date('2024-12-17'),
          time: '9:00 AM - 10:30 AM',
          conferenceId,
          createdAt: new Date('2024-12-01'),
          updatedAt: new Date('2024-12-01'),
        },
      },
      {
        id: '8',
        item: {
          title: 'Security in Modern Web Applications',
          presenter: 'James Wilson',
          presenterAvatar: 'https://ui-avatars.com/api/?name=James+Wilson&background=FF7043&color=fff',
          room: 'Innovation Lab',
          date: new Date('2024-12-17'),
          time: '11:00 AM - 12:30 PM',
          conferenceId,
          createdAt: new Date('2024-12-01'),
          updatedAt: new Date('2024-12-01'),
        },
      },
      {
        id: '9',
        item: {
          title: 'Microservices Architecture',
          presenter: 'Lisa Anderson',
          presenterAvatar: 'https://ui-avatars.com/api/?name=Lisa+Anderson&background=42A5F5&color=fff',
          room: 'Grand Ballroom A',
          date: new Date('2024-12-17'),
          time: '2:00 PM - 3:30 PM',
          conferenceId,
          createdAt: new Date('2024-12-01'),
          updatedAt: new Date('2024-12-01'),
        },
      },
      {
        id: '10',
        item: {
          title: 'Mobile App Development Trends',
          presenter: 'Christopher Brown',
          presenterAvatar: 'https://ui-avatars.com/api/?name=Christopher+Brown&background=66BB6A&color=fff',
          room: 'Conference Room B',
          date: new Date('2024-12-17'),
          time: '4:00 PM - 5:30 PM',
          conferenceId,
          createdAt: new Date('2024-12-01'),
          updatedAt: new Date('2024-12-01'),
        },
      },
    ];

    // Apply search filter if provided
    let filteredItems = [...mockSessions];
    if (queryParams.filter) {
      const filter = queryParams.filter.toLowerCase();
      filteredItems = filteredItems.filter(
        (session) =>
          session.item.title.toLowerCase().includes(filter) ||
          session.item.presenter.toLowerCase().includes(filter) ||
          session.item.room.toLowerCase().includes(filter)
      );
    }

    const totalCount = filteredItems.length;

    // Apply pagination
    if (queryParams.take && queryParams.skip !== undefined) {
      const skip = Number(queryParams.skip);
      const take = Number(queryParams.take);
      filteredItems = filteredItems.slice(skip, skip + take);
    }

    return of({
      items: filteredItems,
      total: totalCount,
      take: queryParams.take || filteredItems.length,
      skip: queryParams.skip || 0,
    }).pipe(delay(300));
  }

  getConferenceLogs(conferenceId: string, queryParams: QueryOptions): Observable<QueryResult<ConferenceLogDto>> {
    const mockLogs: QueryResultItem<ConferenceLogDto>[] = [
      {
        id: '1',
        item: {
          title: 'Opening Keynote Success',
          note: 'The opening keynote was fantastic! Over 500 attendees showed up for the morning session. The energy in the room was incredible.',
          dateTime: new Date('2024-12-15T09:30:00'),
          user: {
            id: 'user1',
            name: 'Sarah Johnson',
            avatar: 'https://ui-avatars.com/api/?name=Sarah+Johnson&background=42A5F5&color=fff',
            initials: 'SJ',
          },
          imageUrl: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800',
          conferenceId,
          createdAt: new Date('2024-12-15T09:35:00'),
          updatedAt: new Date('2024-12-15T09:35:00'),
        },
      },
      {
        id: '2',
        item: {
          title: 'Networking Break Highlights',
          note: 'Great networking session during the break. Many connections were made between attendees and vendors. The coffee was excellent too!',
          dateTime: new Date('2024-12-15T10:45:00'),
          user: {
            id: 'user2',
            name: 'Michael Chen',
            avatar: 'https://ui-avatars.com/api/?name=Michael+Chen&background=66BB6A&color=fff',
            initials: 'MC',
          },
          imageUrl: 'https://images.unsplash.com/photo-1511578314322-379afb476865?w=800',
          conferenceId,
          createdAt: new Date('2024-12-15T10:50:00'),
          updatedAt: new Date('2024-12-15T10:50:00'),
        },
      },
      {
        id: '3',
        item: {
          title: 'Lunch Session Feedback',
          note: 'The lunch provided was outstanding. Attendees were very pleased with the variety and quality. Great job by the catering team!',
          dateTime: new Date('2024-12-15T12:30:00'),
          user: {
            id: 'user3',
            name: 'Emily Rodriguez',
            avatar: 'https://ui-avatars.com/api/?name=Emily+Rodriguez&background=FFA726&color=fff',
            initials: 'ER',
          },
          imageUrl: 'https://images.unsplash.com/photo-1556912172-45b7abe8b7e4?w=800',
          conferenceId,
          createdAt: new Date('2024-12-15T12:35:00'),
          updatedAt: new Date('2024-12-15T12:35:00'),
        },
      },
      {
        id: '4',
        item: {
          title: 'Afternoon Workshop Notes',
          note: 'The Angular 18 workshop was a huge hit! Participants were engaged and asking great questions. The presenter did an excellent job.',
          dateTime: new Date('2024-12-15T14:00:00'),
          user: {
            id: 'user4',
            name: 'David Kim',
            avatar: 'https://ui-avatars.com/api/?name=David+Kim&background=AB47BC&color=fff',
            initials: 'DK',
          },
          imageUrl: 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=800',
          conferenceId,
          createdAt: new Date('2024-12-15T14:05:00'),
          updatedAt: new Date('2024-12-15T14:05:00'),
        },
      },
      {
        id: '5',
        item: {
          title: 'Vendor Expo Update',
          note: 'The vendor expo is buzzing with activity. Many booths are seeing high traffic. Great engagement from attendees.',
          dateTime: new Date('2024-12-15T15:30:00'),
          user: {
            id: 'user5',
            name: 'Jessica Martinez',
            avatar: 'https://ui-avatars.com/api/?name=Jessica+Martinez&background=EF5350&color=fff',
            initials: 'JM',
          },
          imageUrl: 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=800',
          conferenceId,
          createdAt: new Date('2024-12-15T15:35:00'),
          updatedAt: new Date('2024-12-15T15:35:00'),
        },
      },
      {
        id: '6',
        item: {
          title: 'Day 2 Morning Session',
          note: 'Day 2 started strong with a great turnout. The Machine Learning session was particularly well-received. Standing room only!',
          dateTime: new Date('2024-12-16T09:15:00'),
          user: {
            id: 'user6',
            name: 'Robert Taylor',
            avatar: 'https://ui-avatars.com/api/?name=Robert+Taylor&background=26A69A&color=fff',
            initials: 'RT',
          },
          imageUrl: 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=800',
          conferenceId,
          createdAt: new Date('2024-12-16T09:20:00'),
          updatedAt: new Date('2024-12-16T09:20:00'),
        },
      },
      {
        id: '7',
        item: {
          title: 'Panel Discussion Success',
          note: 'The panel discussion on cloud architecture was insightful. Panelists shared valuable real-world experiences. Great Q&A session afterward.',
          dateTime: new Date('2024-12-16T11:00:00'),
          user: {
            id: 'user7',
            name: 'Amanda White',
            avatar: 'https://ui-avatars.com/api/?name=Amanda+White&background=5C6BC0&color=fff',
            initials: 'AW',
          },
          imageUrl: 'https://images.unsplash.com/photo-1511578314322-379afb476865?w=800',
          conferenceId,
          createdAt: new Date('2024-12-16T11:05:00'),
          updatedAt: new Date('2024-12-16T11:05:00'),
        },
      },
      {
        id: '8',
        item: {
          title: 'Security Workshop Highlights',
          note: 'The security workshop was eye-opening. Attendees learned about modern threats and best practices. Very practical and actionable content.',
          dateTime: new Date('2024-12-16T14:30:00'),
          user: {
            id: 'user8',
            name: 'James Wilson',
            avatar: 'https://ui-avatars.com/api/?name=James+Wilson&background=FF7043&color=fff',
            initials: 'JW',
          },
          imageUrl: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800',
          conferenceId,
          createdAt: new Date('2024-12-16T14:35:00'),
          updatedAt: new Date('2024-12-16T14:35:00'),
        },
      },
      {
        id: '9',
        item: {
          title: 'Final Day Opening',
          note: 'Last day of the conference! Great energy as we start the final sessions. Attendees are making the most of their remaining time.',
          dateTime: new Date('2024-12-17T09:00:00'),
          user: {
            id: 'user9',
            name: 'Lisa Anderson',
            avatar: 'https://ui-avatars.com/api/?name=Lisa+Anderson&background=42A5F5&color=fff',
            initials: 'LA',
          },
          imageUrl: 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=800',
          conferenceId,
          createdAt: new Date('2024-12-17T09:05:00'),
          updatedAt: new Date('2024-12-17T09:05:00'),
        },
      },
      {
        id: '10',
        item: {
          title: 'Conference Closing Remarks',
          note: 'What an amazing conference! Over 1200 attendees, 45 vendors, and countless connections made. Thank you to everyone who made this possible. See you next year!',
          dateTime: new Date('2024-12-17T16:00:00'),
          user: {
            id: 'user10',
            name: 'Christopher Brown',
            avatar: 'https://ui-avatars.com/api/?name=Christopher+Brown&background=66BB6A&color=fff',
            initials: 'CB',
          },
          imageUrl: 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=800',
          conferenceId,
          createdAt: new Date('2024-12-17T16:05:00'),
          updatedAt: new Date('2024-12-17T16:05:00'),
        },
      },
    ];

    // Apply search filter if provided
    let filteredItems = [...mockLogs];
    if (queryParams.filter) {
      const filter = queryParams.filter.toLowerCase();
      filteredItems = filteredItems.filter(
        (log) =>
          log.item.title.toLowerCase().includes(filter) ||
          log.item.note.toLowerCase().includes(filter) ||
          log.item.user.name.toLowerCase().includes(filter)
      );
    }

    // Sort by dateTime descending (most recent first)
    filteredItems.sort((a, b) => {
      return new Date(b.item.dateTime).getTime() - new Date(a.item.dateTime).getTime();
    });

    const totalCount = filteredItems.length;

    // Apply pagination
    if (queryParams.take && queryParams.skip !== undefined) {
      const skip = Number(queryParams.skip);
      const take = Number(queryParams.take);
      filteredItems = filteredItems.slice(skip, skip + take);
    }

    return of({
      items: filteredItems,
      total: totalCount,
      take: queryParams.take || filteredItems.length,
      skip: queryParams.skip || 0,
    }).pipe(delay(300));
  }

  getMostRecentLog(conferenceId: string): Observable<ConferenceLogDto | null> {
    // Get all logs and return the most recent one
    return this.getConferenceLogs(conferenceId, { take: 1, skip: 0 }).pipe(
      map((result) => {
        if (result.items && result.items.length > 0) {
          return result.items[0].item;
        }
        return null;
      })
    );
  }

  getBusinessConnectionsLeaderboard(conferenceId: string): Observable<BusinessConnectionsLeaderboardDto[]> {
    const mockBusinesses: BusinessConnectionsLeaderboardDto[] = [
      {
        id: '1',
        businessName: 'TechCorp Solutions',
        connectionsCount: 245,
        rank: 1,
        logo: 'https://ui-avatars.com/api/?name=TechCorp+Solutions&background=42A5F5&color=fff&size=128',
        industry: 'Technology',
      },
      {
        id: '2',
        businessName: 'InnovateHub',
        connectionsCount: 198,
        rank: 2,
        logo: 'https://ui-avatars.com/api/?name=InnovateHub&background=66BB6A&color=fff&size=128',
        industry: 'Innovation',
      },
      {
        id: '3',
        businessName: 'CloudVantage Systems',
        connectionsCount: 187,
        rank: 3,
        logo: 'https://ui-avatars.com/api/?name=CloudVantage&background=FFA726&color=fff&size=128',
        industry: 'Cloud Services',
      },
      {
        id: '4',
        businessName: 'DataFlow Analytics',
        connectionsCount: 165,
        rank: 4,
        logo: 'https://ui-avatars.com/api/?name=DataFlow&background=AB47BC&color=fff&size=128',
        industry: 'Data Analytics',
      },
      {
        id: '5',
        businessName: 'SecureNet Technologies',
        connectionsCount: 152,
        rank: 5,
        logo: 'https://ui-avatars.com/api/?name=SecureNet&background=EF5350&color=fff&size=128',
        industry: 'Cybersecurity',
      },
      {
        id: '6',
        businessName: 'DevOps Dynamics',
        connectionsCount: 138,
        rank: 6,
        logo: 'https://ui-avatars.com/api/?name=DevOps+Dynamics&background=26A69A&color=fff&size=128',
        industry: 'DevOps',
      },
      {
        id: '7',
        businessName: 'MobileFirst Apps',
        connectionsCount: 124,
        rank: 7,
        logo: 'https://ui-avatars.com/api/?name=MobileFirst&background=5C6BC0&color=fff&size=128',
        industry: 'Mobile Development',
      },
      {
        id: '8',
        businessName: 'AI Innovations Inc',
        connectionsCount: 112,
        rank: 8,
        logo: 'https://ui-avatars.com/api/?name=AI+Innovations&background=FF7043&color=fff&size=128',
        industry: 'Artificial Intelligence',
      },
      {
        id: '9',
        businessName: 'WebCraft Studios',
        connectionsCount: 98,
        rank: 9,
        logo: 'https://ui-avatars.com/api/?name=WebCraft&background=42A5F5&color=fff&size=128',
        industry: 'Web Development',
      },
      {
        id: '10',
        businessName: 'Blockchain Ventures',
        connectionsCount: 87,
        rank: 10,
        logo: 'https://ui-avatars.com/api/?name=Blockchain+Ventures&background=66BB6A&color=fff&size=128',
        industry: 'Blockchain',
      },
    ];

    return of(mockBusinesses).pipe(delay(300));
  }

  getAttendeeConnectionsLeaderboard(conferenceId: string): Observable<AttendeeConnectionsLeaderboardDto[]> {
    const mockAttendees: AttendeeConnectionsLeaderboardDto[] = [
      {
        id: '1',
        attendeeName: 'Sarah Johnson',
        connectionsCount: 89,
        rank: 1,
        avatar: 'https://ui-avatars.com/api/?name=Sarah+Johnson&background=42A5F5&color=fff',
        initials: 'SJ',
        company: 'TechCorp Solutions',
        title: 'Senior Developer',
      },
      {
        id: '2',
        attendeeName: 'Michael Chen',
        connectionsCount: 76,
        rank: 2,
        avatar: 'https://ui-avatars.com/api/?name=Michael+Chen&background=66BB6A&color=fff',
        initials: 'MC',
        company: 'InnovateHub',
        title: 'CTO',
      },
      {
        id: '3',
        attendeeName: 'Emily Rodriguez',
        connectionsCount: 68,
        rank: 3,
        avatar: 'https://ui-avatars.com/api/?name=Emily+Rodriguez&background=FFA726&color=fff',
        initials: 'ER',
        company: 'CloudVantage Systems',
        title: 'Product Manager',
      },
      {
        id: '4',
        attendeeName: 'David Kim',
        connectionsCount: 62,
        rank: 4,
        avatar: 'https://ui-avatars.com/api/?name=David+Kim&background=AB47BC&color=fff',
        initials: 'DK',
        company: 'DataFlow Analytics',
        title: 'Data Scientist',
      },
      {
        id: '5',
        attendeeName: 'Jessica Martinez',
        connectionsCount: 58,
        rank: 5,
        avatar: 'https://ui-avatars.com/api/?name=Jessica+Martinez&background=EF5350&color=fff',
        initials: 'JM',
        company: 'SecureNet Technologies',
        title: 'Security Architect',
      },
      {
        id: '6',
        attendeeName: 'Robert Taylor',
        connectionsCount: 54,
        rank: 6,
        avatar: 'https://ui-avatars.com/api/?name=Robert+Taylor&background=26A69A&color=fff',
        initials: 'RT',
        company: 'DevOps Dynamics',
        title: 'DevOps Engineer',
      },
      {
        id: '7',
        attendeeName: 'Amanda White',
        connectionsCount: 49,
        rank: 7,
        avatar: 'https://ui-avatars.com/api/?name=Amanda+White&background=5C6BC0&color=fff',
        initials: 'AW',
        company: 'MobileFirst Apps',
        title: 'Mobile Developer',
      },
      {
        id: '8',
        attendeeName: 'James Wilson',
        connectionsCount: 45,
        rank: 8,
        avatar: 'https://ui-avatars.com/api/?name=James+Wilson&background=FF7043&color=fff',
        initials: 'JW',
        company: 'AI Innovations Inc',
        title: 'AI Researcher',
      },
      {
        id: '9',
        attendeeName: 'Lisa Anderson',
        connectionsCount: 41,
        rank: 9,
        avatar: 'https://ui-avatars.com/api/?name=Lisa+Anderson&background=42A5F5&color=fff',
        initials: 'LA',
        company: 'WebCraft Studios',
        title: 'Full Stack Developer',
      },
      {
        id: '10',
        attendeeName: 'Christopher Brown',
        connectionsCount: 38,
        rank: 10,
        avatar: 'https://ui-avatars.com/api/?name=Christopher+Brown&background=66BB6A&color=fff',
        initials: 'CB',
        company: 'Blockchain Ventures',
        title: 'Blockchain Developer',
      },
    ];

    return of(mockAttendees).pipe(delay(300));
  }

  createConference(conference: ConferenceDto): Observable<ProcessResult> {
    // Generate a new ID
    const newId = String(this.mockConferences.length + 1);
    const newConference: QueryResultItem<ConferenceDto> = {
      id: newId,
      item: {
        ...conference,
        id: newId,
        isActive: false,
        isUpcoming: true,
        createdAt: new Date(),
        updatedAt: new Date(),
      },
    };

    // Add to mock conferences
    this.mockConferences.push(newConference);

    return of({
      success: true,
      message: 'Conference created successfully',
      id: newId,
    }).pipe(delay(500));
  }
}

